import React, { useEffect, useState } from "react";
import { CabinetsWrapper } from "./styled";
import {
  Box,
  ButtonStyled,
  SubHeadingStyled,
  ThemeLoader,
} from "../../../components/styled/Elements";
import { useTranslation } from "react-i18next";
import FloatingInput from "../../../components/common/inputFields/FloatingInput";
import {
  PlaceholderImg,
  PlusIcon,
  SearchIcon,
} from "../../../components/styled/AllImages";
import ListTable from "../../../components/common/tables/ListTable";
import HistoryModal from "../../../components/admin/Modals/HistoryModal/HistoryModal";
import { useNavigate } from "react-router-dom";
import TransferModal from "./components/TransferModal";
import { cabinetColumns } from "../../../helpers/adminConstants/TableConstants";
import DeleteModal from "../../../components/admin/Modals/DeleteModal/DeleteModal";
import CabinetsApi from "../../../services/api/cabinetsApi";
import { toast } from "react-toastify";
import {
  getFormattedDate,
  getFormattedTime,
  getModuleOperations,
  hasPlanFeature,
  setIsTemporary,
} from "../../../utils";
import { useDebounce } from "../../../hooks/useDebounce";
import { useForm } from "react-hook-form";
import { stepsToTransfer } from "../../../helpers/adminConstants/AdminConstants";
import { useUser } from "../../../context/userContext";
import RequestModal from "../../../components/admin/Modals/RequestModal/RequestModal";
import RequestsApi from "../../../services/api/requestsApi";
import UsersApi from "../../../services/api/usersApi";
import VisibilityModal from "../../../components/admin/Modals/DeleteModal/VisibilityModal";

const Cabinets = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm();

  const { user } = useUser();
  const userId = Number(user?.id);
  const userTypeId = Number(user?.user_type_id);

  const navigate = useNavigate();
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [perPage, setPerPage] = useState(null);
  const [rowsData, setRowsData] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [cabinetId, setCabinetId] = useState(null); // Store selected cabinet ID
  const [loading, setLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [historyData, setHistoryData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [transferSelections, setTransferSelections] = useState(
    Array(stepsToTransfer.length).fill(null)
  );
  const [visibilityConfirm, setVisibilityConfirm] = useState(false);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [visibilityForTemp, setVisibilityForTemp] = useState(false);
  const ASSET_URL = process.env.REACT_APP_ASSET_URL;
  const filteredColumns = cabinetColumns.filter((col) => col.name !== "timer");
  let options = [
    "open",
    "info",
    "edit",
    "transfer",
    "status",
    "delete",
    // "expiry",
  ];
  // const filteredOptions =
  //   parseInt(user?.id) === 6
  //     ? options.filter((option) => option !== "expiry")
  //     : options;

  const handleCreateCabinet = () => {
    console.log('user: ', user);
    if (
      user?.office_location &&
      user?.office_location?.id &&
      user?.department &&
      user?.department?.id
    )
      navigate("/cabinets/add-cabinet", { replace: true });
    else if (Number(user?.user_type_id) === 6)
      navigate("/cabinets/add-cabinet", { replace: true });
    else toast.error(t("please_assign_yourself_department"));
  };

  const handleEdit = (row) => {
    navigate(`/cabinets/edit-cabinet/${row.hash_id}`);
  };
  const handleInfo = (row) => {
    getHistoryData(row.hash_id);
    setShowHistoryModal(true);
  };

  const handleTransfer = (row) => {
    setCabinetId(row.id);
    setShowTransferModal(true);
  };

  const onDelete = (row) => {
    setCabinetId(row.hash_id);
    setShowDeleteModal(true);
  };

  const onVisibility = async (row, tempFlag) => {
    setCabinetId(row?.id);
    setVisibilityForTemp(tempFlag);
    setVisibilityConfirm(true);
  };

  const handleDelete = async () => {
    try {
      const response = await CabinetsApi.deleteCabinet(cabinetId);
      if (response.code === 200) {
        toast.success(t("cabinet_deleted"));
        setShowDeleteModal(false);
        setCabinetId(null);
        getCabinetsList();
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
      if (response.code === 400) {
        toast.error(response?.error);
      }
    } catch (error) {}
  };

  const handleRequest = async () => {
    try {
      setRequestLoading(true);
      const { code, message } = await RequestsApi.createRequest({
        module_id: 1,
        module_reference_id: cabinetId,
      });
      if (code === 200) {
        toast.success(t("req_sent_successful"));
        setShowRequestModal(false);
      }
      if (code === 100 || code === 204) {
        toast.error(message);
      }
    } catch (error) {
    } finally {
      setRequestLoading(false);
    }
  };

  const handleView = (row) => {
    console.log('cabin row: ', row);
    setCabinetId(row.id);

    const {
      current_owner,
      current_owner_department_id,
      cabinet_details,
      is_temporary,
      access_granted_requests,
      user_have_child_access,
    } = row || {};

    const { department } = user || {};
    const departmentId = Number(department?.id);

    // 1. Basic Owner Check
    const isLatestOwner = current_owner && userId === Number(current_owner);

    // 2. Access via Cabinet Details Logic
    // We check if ANY detail record grants access based on its own setting_id
    const isOpenToUser = cabinet_details?.some((detail) => {
      const sId = detail?.setting_id;
      const selectedUsers = detail?.selected_users || [];
      const selectedDepts = detail?.selected_departments;

      // Check User Selection for this specific detail
      const isUserInThisDetail = selectedUsers.includes(userId);

      // Check Department Selection for this specific detail
      const isDeptInThisDetail = Array.isArray(selectedDepts)
        ? selectedDepts.includes(departmentId)
        : (selectedDepts !== null && selectedDepts !== undefined && Number(selectedDepts) === departmentId);

      // Apply rules based on this detail's setting_id
      return (
        (sId === 3 && Number(current_owner_department_id) === departmentId) ||
        ([4, 5, 8, 9].includes(sId) && isUserInThisDetail) ||
        ([6, 7].includes(sId) && isDeptInThisDetail)
      );
    }) ?? false;

    // 3. Access via Requests
    const hasAccessThroughRequest = Array.isArray(access_granted_requests) &&
      access_granted_requests.some((item) => Number(item?.from_user_id) === userId);

    console.log('userId: ', userId, 'isLatestOwner: ', isLatestOwner, 'isOpenToUser: ', isOpenToUser, 'hasAccessThroughRequest: ', hasAccessThroughRequest, 'user_have_child_access: ', user_have_child_access, "userTypeId: ", userTypeId);

    // 4. Final Permission Check
    if (
      isLatestOwner ||
      isOpenToUser ||
      hasAccessThroughRequest ||
      user_have_child_access ||
      userTypeId === 6
    ) {
      navigate(`/cabinets/${row.hash_id}/files`);
      setIsTemporary(is_temporary);
    } else {
      if (hasPlanFeature(4)) {
        setShowRequestModal(true);
      } else {
        toast.error(t("no_access_to_open_or_request"));
      }
    }
  };

  const handleStatusToggle = async (row) => {
    const newObj = {
      cabinet_id: row.id,
      status: parseInt(row.status) === 1 ? 0 : 1,
      is_temporary: 0
    };
    try {
      const response = await CabinetsApi.updateCabinetStatus(newObj);
      if (response.code === 200) {
        toast.success(t("cabinet_status_updated"));
        getCabinetsList();
      } else {
        toast.error(response.message);
      }
    } catch (error) {}
  };
  const handleSearch = (e) => {
    const query = e.target.value.trim();
    setSearchQuery(query);
  };

  const getCabinetsList = async (page = 1, query = "") => {
    try {
      setLoading(true);
      const { data, code } = await CabinetsApi.cabinetsList(page, query);
      if (code === 200) {
        const formattedData = data?.data?.map((item) => ({
          ...item,
          owner_user: {
            ...item?.owner_user,
            profile: item?.owner_user?.profile
              ? `${ASSET_URL}/${item?.owner_user?.profile}`
              : PlaceholderImg,
            subLabel:
              parseInt(user?.id) !== item?.owner_user?.id &&
              item?.owner_user?.user_detail?.department_selective &&
              item?.owner_user?.user_detail?.office_location_selective
                ? language === "en"
                  ? `${item.owner_user.user_detail.department_selective.name_en} (${item.owner_user.user_detail.office_location_selective.name_en})`
                  : `${item.owner_user.user_detail.department_selective.name_ar} (${item.owner_user.user_detail.office_location_selective.name_ar})`
                : "",
            name:
              parseInt(user?.id) === parseInt(item?.owner_user?.id)
                ? t("myself")
                : item?.owner_user?.name,
          },
        }));
        setRowsData(formattedData);
        setTotalCount(data?.total);
        setPerPage(data?.per_page);
      } else {
        setRowsData([]);
        setTotalCount(null);
        setPerPage(null);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getHistoryData = async (id) => {
    try {
      setHistoryLoading(true);
      const { data, code } = await CabinetsApi.getCabinetHistory(id);

      if (code === 200) {
        const createdBy = {
          id: data?.created_by_user?.id,
          name: data?.created_by_user?.name,
          subLabel: data?.created_by_user?.user_detail
            ? `${
                language === "en"
                  ? data?.created_by_user?.user_detail?.department_selective
                      .name_en
                  : data?.created_by_user?.user_detail?.department_selective
                      .name_ar
              } (${
                language === "en"
                  ? data?.created_by_user?.user_detail
                      ?.office_location_selective.name_en
                  : data?.created_by_user?.user_detail
                      ?.office_location_selective.name_ar
              })`
            : "",
          avatar: data?.created_by_user?.profile
            ? `${ASSET_URL}/${data?.created_by_user?.profile}`
            : PlaceholderImg,
          date: getFormattedDate(data?.created_at),
          time: getFormattedTime(data?.created_at),
        };

        const editHistory =
          data?.update_history?.length > 0 &&
          data?.update_history?.map((user) => ({
            id: user?.updated_by?.id,
            name: user?.updated_by?.name,
            subLabel: user.updated_by.user_detail
              ? `${
                  language === "en"
                    ? user.updated_by.user_detail.department_selective.name_en
                    : user.updated_by.user_detail.department_selective.name_ar
                } (${
                  language === "en"
                    ? user.updated_by.user_detail.office_location_selective
                        .name_en
                    : user.updated_by.user_detail.office_location_selective
                        .name_ar
                })`
              : "",
            avatar: user?.updated_by?.profile
              ? `${ASSET_URL}/${user?.updated_by?.profile}`
              : PlaceholderImg,
            date: getFormattedDate(user.created_at),
            time: getFormattedTime(user.created_at),
          }));

        const transferHistory =
          data?.transfer_histories.length > 0 &&
          data?.transfer_histories.map((transUser) => ({
            from: {
              id: transUser?.transfer_by?.id,
              name: transUser?.transfer_by?.name,
              subLabel: transUser?.transfer_by?.user_detail
                ? `${
                    language === "en"
                      ? transUser?.transfer_by?.user_detail.department_selective
                          .name_en
                      : transUser?.transfer_by?.user_detail.department_selective
                          .name_ar
                  } (${
                    language === "en"
                      ? transUser?.transfer_by?.user_detail
                          .office_location_selective.name_en
                      : transUser?.transfer_by?.user_detail
                          ?.office_location_selective.name_ar
                  })`
                : "",
              avatar: transUser?.transfer_by?.profile
                ? `${ASSET_URL}/${transUser?.transfer_by?.profile}`
                : PlaceholderImg,
            },
            to: {
              id: transUser?.transfer_to?.id,
              name: transUser?.transfer_to?.name,
              subLabel: transUser?.transfer_to?.user_detail
                ? `${
                    language === "en"
                      ? transUser?.transfer_to?.user_detail.department_selective
                          .name_en
                      : transUser?.transfer_to?.user_detail.department_selective
                          .name_ar
                  } (${
                    language === "en"
                      ? transUser?.transfer_to?.user_detail
                          .office_location_selective.name_en
                      : transUser?.transfer_to?.user_detail
                          ?.office_location_selective.name_ar
                  })`
                : "",
              avatar: transUser?.transfer_to?.profile
                ? `${ASSET_URL}/${transUser?.transfer_to?.profile}`
                : PlaceholderImg,
            },
            date: getFormattedDate(transUser.created_at),
            time: getFormattedTime(transUser.created_at),
          }));

        setHistoryData((prevData) => ({
          ...prevData,
          createdBy: createdBy,
          editHistory: editHistory,
          transferHistory: transferHistory,
        }));
      }
    } catch (error) {
    } finally {
      setHistoryLoading(false);
    }
  };
  const onSubmit = async (formData) => {
    const selectedRadio = document.querySelector(
      'input[name="trans_user_select"]:checked'
    );
    const selectedId = selectedRadio ? parseInt(selectedRadio.value, 10) : null;
    const updatedData = {
      ...formData,
      action: "create",
      cabinet_id: cabinetId,
      sent_to: selectedId,
    };
    try {
      setTransferLoading(true);
      const response = await CabinetsApi.transferCabinet(updatedData);
      if (response.code === 200) {
        toast.success(`${t("cabinet_transfer_successful")}`);
        reset();
        // onHide();
        setShowTransferModal(false);
        setCurrentStep(0);
        setTransferSelections(Array(stepsToTransfer.length).fill(null));
        getCabinetsList(activePage);
        // navigate("/cabinets")
      }
      if (response.code === 100 || response.code === 204) {
        toast.error(response.message);
      }
    } catch (error) {
    } finally {
      setTransferLoading(false);
    }
  };

  const updateVisibilityStatus = async () => {
    try {
      const res = await CabinetsApi.updateVisibilityStatus(
        cabinetId,
        visibilityForTemp
      );
      if (res.code === 200) {
        toast.success(t("visibility_updated"));
        setVisibilityConfirm(false);
        getCabinetsList(activePage, debouncedSearchQuery);
      }
    } catch (error) {}
  };

  useEffect(() => {
    getCabinetsList(activePage, debouncedSearchQuery);
  }, [activePage, debouncedSearchQuery, language]);

  return (
    <CabinetsWrapper>
      <Box className="content-head">
        <SubHeadingStyled size="16px" weight="700" color="var(--text)">
          {t("cabinets")}
        </SubHeadingStyled>
        <Box className="d-flex gap-2 head-item-two">
          <FloatingInput
            name="search"
            type="input"
            fieldType="text"
            label={t("search")}
            placeholder={t("search")}
            icon={SearchIcon}
            onChange={handleSearch}
          />
          {getModuleOperations(1, 2) && (
            <ButtonStyled
              label={t("add_cabinet")}
              icon={PlusIcon}
              onClick={handleCreateCabinet}
            />
          )}
        </Box>
      </Box>
      <Box>
        {loading ? (
          <ThemeLoader />
        ) : (
          <ListTable
            options={options}
            typeId={1}
            columns={filteredColumns}
            rows={rowsData}
            onEdit={handleEdit}
            onDelete={onDelete}
            onVisibility={onVisibility}
            onOpen={handleView}
            onInfoView={handleInfo}
            onStatusToggle={handleStatusToggle}
            onTransfer={handleTransfer}
            totalCount={totalCount}
            perPage={perPage}
            activePage={activePage}
            setActivePage={setActivePage}
            updateVisibilityStatus={updateVisibilityStatus}
          />
        )}
      </Box>
      <HistoryModal
        title={t("cabinet_history")}
        show={showHistoryModal}
        onHide={() => setShowHistoryModal(false)}
        createdBy={historyData.createdBy}
        editHistory={historyData.editHistory}
        transferHistory={historyData.transferHistory}
        historyLoading={historyLoading}
      />
      <TransferModal
        onSubmit={onSubmit}
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        transferLoading={transferLoading}
        selections={transferSelections}
        setSelections={setTransferSelections}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        // handleTransIds={handleTransIds} // Function to capture selected member IDs
        // cabinetId={cabinetId} // Pass cabinet ID to modal
      />
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        title={t("Cabinet")}
      />
      <VisibilityModal
        show={visibilityConfirm}
        onHide={() => setVisibilityConfirm(false)}
        onVisibility={updateVisibilityStatus}
        title={t("Cabinet")}
      />
      <RequestModal
        title={"Cabinet"}
        show={showRequestModal}
        onHide={() => setShowRequestModal(false)}
        handleRequest={handleRequest}
        isLoading={requestLoading}
      />
    </CabinetsWrapper>
  );
};

export default Cabinets;
